import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../contexts/AuthContext'
import { usePageTitle } from '../hooks/usePageTitle'
import { useOutings } from '../hooks/useOutings'
import { useStandingTeeTimes } from '../hooks/useStandingTeeTimes'
import {
  AreaSectionHead,
  ClubButton,
  ClubPage,
  Container,
  EmptyPlate,
  PageHead,
  SignInPrompt,
  StandingTeeTimes,
} from '../components/golf'
import { cn } from '../lib/cn'
import { formatCents } from '../lib/clubMoney'
import { MAX_INVITES, parseInviteContacts } from '../lib/inviteContacts'
import type {
  BookingCard,
  GolfCourse,
  GolfCoursesResponse,
  InviteContact,
  Outing,
  OutingStatus,
  TeeSlot,
} from '../lib/types'

/**
 * The Outings desk (`/clubhouse/outings`) — the tee-time MVP's organizer
 * console. The sheet scrolls the main column; the rail (the-outings card)
 * rides sticky beside it holding the draft outing and the book, so the plan
 * is never buried under the day's chips. The club never takes the booking
 * itself — it only holds the thread until the round is marked booked AT THE
 * COURSE.
 *
 * Publicly browsable like the rest of the clubhouse: a visitor sees the page
 * with the console standing in as a sign-in invitation (SignInPrompt), the
 * same split every member area makes.
 *
 * Geometry follows the card; the full stationery pass (day strip, banded
 * times, seat chips) comes later.
 */

/**
 * A tee time seats a foursome — the operator sells starts in fours, so this is
 * the course's rule, not ours. Mirrors `Fireside::Outing::MAX_SLOTS`, which is
 * where it is actually enforced.
 */
const MAX_PARTY_SIZE = 4

/** The area presets — starting points, not a service area. */
const AREA_PRESETS = [
  { label: 'Queens', lat: 40.73, lng: -73.79 },
  { label: 'Brooklyn', lat: 40.65, lng: -73.95 },
  { label: 'Manhattan', lat: 40.78, lng: -73.97 },
  { label: 'Bronx', lat: 40.86, lng: -73.87 },
  { label: 'Staten Island', lat: 40.58, lng: -74.15 },
  { label: 'Nassau', lat: 40.73, lng: -73.59 },
] as const

const MY_LOCATION_LABEL = 'Your location'

interface PickedLocation {
  label: string
  lat: number
  lng: number
}

/** Local-clock YYYY-MM-DD — never toISOString, which shifts across midnight UTC. */
function toDateInputValue(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** The coming Saturday — strictly ahead, so on a Saturday it means the next one. */
function nextSaturday(from: Date): Date {
  const days = (6 - from.getDay() + 7) % 7 || 7
  const d = new Date(from)
  d.setDate(d.getDate() + days)
  return d
}

function daysFrom(from: Date, days: number): Date {
  const d = new Date(from)
  d.setDate(d.getDate() + days)
  return d
}

/** "Sat, Aug 8 · 7:10 AM" — the outing's one fact, kept readable everywhere. */
function formatTeeTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  const day = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date)
  const time = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
  return `${day} · ${time}`
}

/** "5:55" + "AM" split apart — the chip sets the clock large and the meridiem small. */
function chipClock(iso: string): { clock: string; meridiem: string } {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return { clock: iso, meridiem: '' }
  const parts = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).formatToParts(date)
  return {
    clock: parts
      .filter((p) => p.type === 'hour' || p.type === 'minute')
      .map((p) => p.value)
      .join(':'),
    meridiem: parts.find((p) => p.type === 'dayPeriod')?.value ?? '',
  }
}

/**
 * The sheet's day, told in four bands (the-outings card) — a wall of eighty
 * chips reads as noise, the same eighty grouped under Morning/Midday/
 * Afternoon/Twilight heads with counts reads as a day.
 */
const BANDS = [
  { label: 'Morning', until: 10 },
  { label: 'Midday', until: 13 },
  { label: 'Afternoon', until: 16 },
  { label: 'Twilight', until: 24 },
] as const

function bandLabel(iso: string): string {
  const hour = new Date(iso).getHours()
  return (BANDS.find((band) => hour < band.until) ?? BANDS[BANDS.length - 1]).label
}

/**
 * The day at a glance — one thin cell per half hour, 5:30am to 7:30pm, lit
 * where the sheet has open seats. A folded course tells its whole day in one
 * strip instead of asking for a scroll through its chips.
 */
const STRIP_START_MIN = 330
const STRIP_END_MIN = 1170
const STRIP_CELL_MIN = 30

function stripCells(
  slots: TeeSlot[],
  pickedTeeOffAt: string | null,
): ('open' | 'sel' | 'closed')[] {
  const cells = Array.from(
    { length: (STRIP_END_MIN - STRIP_START_MIN) / STRIP_CELL_MIN },
    () => 'closed' as 'open' | 'sel' | 'closed',
  )
  for (const slot of slots) {
    const date = new Date(slot.tee_off_at)
    if (Number.isNaN(date.getTime())) continue
    const cell = Math.floor(
      (date.getHours() * 60 + date.getMinutes() - STRIP_START_MIN) / STRIP_CELL_MIN,
    )
    if (cell < 0 || cell >= cells.length) continue
    if (slot.tee_off_at === pickedTeeOffAt) cells[cell] = 'sel'
    else if (cells[cell] !== 'sel' && slot.open_slots > 0) cells[cell] = 'open'
  }
  return cells
}

function DayStrip({ slots, pickedTeeOffAt }: { slots: TeeSlot[]; pickedTeeOffAt: string | null }) {
  return (
    <div className="mb-1 mt-3.5" aria-hidden="true">
      <div className="flex h-3 gap-[2px]">
        {stripCells(slots, pickedTeeOffAt).map((state, i) => (
          <i
            key={i}
            className={cn(
              'flex-1 rounded-club-sm',
              state === 'closed' && 'bg-paper-deep',
              state === 'open' && 'bg-fairway',
              state === 'sel' && 'bg-pine ring-1 ring-gilt',
            )}
          />
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[9.5px] uppercase tracking-[0.14em] text-brass-deep tabular-nums">
        <span>5:30</span>
        <span>9a</span>
        <span>12p</span>
        <span>3p</span>
        <span>7p</span>
      </div>
    </div>
  )
}

const STATUS_CHIP: Record<OutingStatus, string> = {
  proposed: 'border-brass text-brass-deep',
  confirmed: 'border-fairway text-fairway',
  booked: 'border-fairway bg-sage-soft text-fairway',
  cancelled: 'border-ink-soft/35 text-ink-soft',
}

function StatusBadge({ status }: { status: OutingStatus }) {
  return (
    <span
      className={cn(
        'inline-block whitespace-nowrap rounded-club-sm border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em]',
        STATUS_CHIP[status],
      )}
    >
      {status}
    </span>
  )
}

const inputClass =
  'w-full rounded-club border border-ink/20 bg-parchment px-[15px] py-[11px] text-[15px] text-ink placeholder:text-ink-soft focus:border-fairway focus:shadow-[0_0_0_3px_rgba(44,90,65,0.14)]'

const labelClass =
  'mb-[7px] block text-xs font-semibold uppercase tracking-[0.16em] text-brass-deep'

/** The inline create form that opens under a picked tee time. */
function CreateOutingForm({
  course,
  slot,
  onCancel,
  onCreate,
}: {
  course: GolfCourse
  slot: TeeSlot
  onCancel: () => void
  onCreate: (body: {
    course_key: string
    tee_off_at: string
    slots: number
    holes: number
    price_cents_per_head: number
    invites: InviteContact[]
  }) => Promise<void>
}) {
  // A tee time seats four, and this one may already be part-sold. Offer only
  // what can actually be booked rather than letting the organizer pick a party
  // the checkout will refuse after everyone has already said yes.
  const partyOptions = useMemo(() => {
    const seats = Math.max(1, Math.min(slot.open_slots, MAX_PARTY_SIZE))
    return Array.from({ length: seats }, (_, i) => i + 1)
  }, [slot.open_slots])
  const [partySize, setPartySize] = useState(() => partyOptions[partyOptions.length - 1])
  const [emailsRaw, setEmailsRaw] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // On a wide viewport the form opens in the sticky rail, already in view —
  // `nearest` makes this a no-op there. Below the wrap point the rail sits
  // under the whole sheet, and this scroll IS the pick's visible feedback.
  // Instant, not smooth: Chrome silently drops smooth scrollIntoView in some
  // window states (observed live on the preview).
  const formRef = useRef<HTMLFormElement>(null)
  useEffect(() => {
    formRef.current?.scrollIntoView({ block: 'nearest' })
  }, [])

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    const { contacts, error: contactError } = parseInviteContacts(emailsRaw)
    if (contactError) {
      setError(contactError)
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await onCreate({
        course_key: course.key,
        tee_off_at: slot.tee_off_at,
        slots: partySize,
        holes: slot.holes,
        price_cents_per_head: slot.price_cents,
        invites: contacts,
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The outing could not be created — try again.')
      setSubmitting(false)
    }
  }

  return (
    <form
      ref={formRef}
      onSubmit={submit}
      className="mt-4 border-t border-brass/25 pt-4"
      aria-label="Create the outing"
    >
      <p className="mb-4 text-[13.5px] text-ink">
        <b className="font-semibold text-pine">{formatTeeTime(slot.tee_off_at)}</b> at{' '}
        {course.name} · {slot.holes} holes · {formatCents(slot.price_cents)} a head
      </p>

      <div className="mb-[18px] max-w-[180px]">
        <label htmlFor="outing-party-size" className={labelClass}>
          Party size
        </label>
        <select
          id="outing-party-size"
          value={partySize}
          onChange={(e) => setPartySize(Number(e.target.value))}
          className={inputClass}
        >
          {partyOptions.map((n) => (
            <option key={n} value={n}>
              {n} players
            </option>
          ))}
        </select>
      </div>

      <div className="mb-[18px]">
        <label htmlFor="outing-invite-emails" className={labelClass}>
          Invitations — one email or phone per line
        </label>
        <textarea
          id="outing-invite-emails"
          value={emailsRaw}
          onChange={(e) => setEmailsRaw(e.target.value)}
          rows={4}
          placeholder={'arnold@example.com\n(646) 555-0142'}
          className={inputClass}
        />
        <p className="mt-[7px] text-[12.5px] text-ink-soft">
          Up to {MAX_INVITES}, one per line — an email or a phone number. Each gets their
          share and a link to answer; no account needed.
        </p>
      </div>

      {error && (
        <p role="alert" className="mb-3.5 text-[12.5px] text-flag">
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        <ClubButton type="submit" size="sm" disabled={submitting}>
          {submitting ? 'Sending…' : 'Propose the outing'}
        </ClubButton>
        <ClubButton type="button" variant="ghost" size="sm" onClick={onCancel}>
          Never mind
        </ClubButton>
      </div>
    </form>
  )
}

/**
 * Pay-and-book, in the club. The card is typed here every time and held
 * nowhere: it lives in this component's state for the length of the form,
 * goes out with one request, and is wiped on success or failure. Nothing
 * saves it, because the course's own checkout has no tokenisation to save it
 * WITH — the alternative to typing it each time would be us warehousing a
 * card number, which is not a trade worth making.
 */
function BookRow({
  outing,
  onBook,
}: {
  outing: Outing
  onBook: (id: string, card: BookingCard) => Promise<{ booked?: false; detail?: string | null; finish_at_course_url?: string | null }>
}) {
  const blank: BookingCard = {
    number: '', exp_month: '', exp_year: '', cvv: '', name: '',
    address_line1: '', postal_code: '', country: 'US', phone: '',
  }
  const [open, setOpen] = useState(false)
  const [card, setCard] = useState<BookingCard>(blank)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notBooked, setNotBooked] = useState<string | null>(null)

  const set = (key: keyof BookingCard) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setCard((prev) => ({ ...prev, [key]: e.target.value }))

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    setNotBooked(null)
    try {
      const result = await onBook(outing.id, card)
      setCard(blank) // never linger, whatever happened
      if (result.booked === false) {
        setNotBooked(result.detail || 'The club could not make this booking — finish at the course.')
        setBusy(false)
        return
      }
      setOpen(false)
    } catch (e) {
      setCard((prev) => ({ ...prev, number: '', cvv: '' })) // drop the sensitive half on failure
      setError(e instanceof Error ? e.message : 'The booking did not go through.')
    }
    setBusy(false)
  }

  if (!outing.bookable_here) {
    return outing.booking_blocked_reason ? (
      <p className="mt-2 text-[12.5px] text-ink-soft">
        The club can&apos;t book this one — {outing.booking_blocked_reason}.
      </p>
    ) : null
  }

  if (!open) {
    return (
      <div className="mt-3">
        <ClubButton size="sm" onClick={() => setOpen(true)}>
          Book it
        </ClubButton>
      </div>
    )
  }

  const field = (key: keyof BookingCard, label: string, extra: Record<string, string> = {}) => (
    <div>
      <label htmlFor={`${key}-${outing.id}`} className={labelClass}>
        {label}
      </label>
      <input
        id={`${key}-${outing.id}`}
        value={card[key]}
        onChange={set(key)}
        autoComplete="off"
        className={cn(inputClass, 'py-[8px] text-[13.5px]')}
        {...extra}
      />
    </div>
  )

  return (
    <form onSubmit={submit} className="mt-3 border-t border-brass/25 pt-3" aria-label="Pay and book">
      <p className="mb-3 text-[12.5px] text-ink-soft">
        Your card goes straight to the course. The club doesn&apos;t keep it — you&apos;ll type it
        each time.
      </p>
      <div className="flex flex-col gap-2.5">
        {field('name', 'Name on card')}
        {field('number', 'Card number', { inputMode: 'numeric' })}
        <div className="flex gap-2.5">
          {field('exp_month', 'Month', { inputMode: 'numeric', placeholder: 'MM' })}
          {field('exp_year', 'Year', { inputMode: 'numeric', placeholder: 'YYYY' })}
          {field('cvv', 'CVV', { inputMode: 'numeric' })}
        </div>
        {field('address_line1', 'Billing address')}
        <div className="flex gap-2.5">
          {field('postal_code', 'Postal code')}
          {field('country', 'Country')}
        </div>
        {field('phone', 'Billing phone', { inputMode: 'tel', autoComplete: 'tel' })}
      </div>

      {error && (
        <p role="alert" className="mt-2.5 text-[12.5px] text-flag">
          {error}
        </p>
      )}
      {notBooked && (
        <p role="status" className="mt-2.5 text-[12.5px] text-ink-soft">
          {notBooked}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-3">
        <ClubButton type="submit" size="sm" disabled={busy}>
          {busy ? 'Booking…' : `Pay & book${outing.total_cents ? ` · ${formatCents(outing.total_cents)}` : ''}`}
        </ClubButton>
        <ClubButton type="button" variant="ghost" size="sm" onClick={() => { setOpen(false); setCard(blank) }}>
          Cancel
        </ClubButton>
      </div>
    </form>
  )
}

/** The optional booking_ref + "Mark booked" row a proposed outing carries. */
function FinalizeRow({
  outing,
  onFinalize,
}: {
  outing: Outing
  onFinalize: (id: string, bookingRef?: string) => Promise<void>
}) {
  const [bookingRef, setBookingRef] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const finalize = async () => {
    setBusy(true)
    setError(null)
    try {
      await onFinalize(outing.id, bookingRef.trim() || undefined)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not mark the outing booked.')
      setBusy(false)
    }
  }

  return (
    <div className="mt-3.5 border-t border-brass/25 pt-3.5">
      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor={`booking-ref-${outing.id}`} className="sr-only">
          Booking reference (optional)
        </label>
        <input
          id={`booking-ref-${outing.id}`}
          value={bookingRef}
          onChange={(e) => setBookingRef(e.target.value)}
          placeholder="Booking ref (optional)"
          className={cn(inputClass, 'max-w-[220px] py-[8px] text-[13.5px]')}
        />
        <ClubButton size="sm" variant="ink" onClick={finalize} disabled={busy}>
          {busy ? 'Marking…' : 'Mark booked'}
        </ClubButton>
        {outing.booking_url && (
          <a
            href={outing.booking_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[13px] font-semibold text-fairway"
          >
            Book at course ↗
          </a>
        )}
      </div>
      {error && (
        <p role="alert" className="mt-2.5 text-[12.5px] text-flag">
          {error}
        </p>
      )}
    </div>
  )
}

/**
 * One outing as a slim rail row — course, tee and status at a glance, the
 * detail (invites, finalize, booking link) behind a single-open disclosure.
 * A sticky rail has to FIT the viewport: a scrollable pane inside one is the
 * classic nested-scrollbar antipattern, so the book reads as a summary, the
 * way a checkout keeps its order rail to line items.
 */
function OutingCard({
  outing,
  onFinalize,
  onBook,
  justProposed = false,
  open,
  onToggle,
  cardRef,
}: {
  outing: Outing
  onFinalize: (id: string, bookingRef?: string) => Promise<void>
  onBook: (id: string, card: BookingCard) => Promise<{ booked?: false; detail?: string | null }>
  /** True on the row a create just put on the book — the arrival moment. */
  justProposed?: boolean
  open: boolean
  onToggle: () => void
  cardRef?: React.Ref<HTMLLIElement>
}) {
  return (
    <li
      ref={cardRef}
      className={cn(
        'rounded-club-lg border bg-parchment px-4 py-3',
        justProposed ? 'border-fairway' : 'border-ink/12',
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 rounded-club-sm text-left"
      >
        <span className="min-w-0">
          <span className="block truncate font-display text-[16px] font-medium text-pine">
            {outing.course_name}
          </span>
          <span className="mt-0.5 block text-[12.5px] text-ink">
            {formatTeeTime(outing.tee_off_at)}
            {outing.holes != null && <> · {outing.holes} holes</>} · {outing.accepted_count} of{' '}
            {outing.slots} in
            {outing.share_cents_each != null && (
              <> · {formatCents(outing.share_cents_each)} each</>
            )}
          </span>
        </span>
        <StatusBadge status={outing.status} />
      </button>

      {justProposed && (
        <p className="mt-1.5 text-[13px] font-semibold text-fairway">
          On the book
          {outing.invites.length > 0
            ? ` — ${outing.invites.length === 1 ? 'the invitation is' : `${outing.invites.length} invitations are`} out.`
            : ' — no invitations yet.'}
        </p>
      )}

      {open && (
        <>
          {outing.invites.length > 0 && (
            <ul className="mt-2.5 flex flex-wrap gap-2">
              {outing.invites.map((invite) => (
                <li
                  key={invite.contact}
                  className="inline-flex items-center gap-1.5 rounded-club-sm border border-ink/15 bg-paper px-2.5 py-1 text-[12px] text-ink"
                >
                  {invite.contact}
                  {invite.share_cents != null && (
                    <span className="text-ink-soft">{formatCents(invite.share_cents)}</span>
                  )}
                  <span
                    className={cn(
                      'text-[10px] font-semibold uppercase tracking-[0.1em]',
                      invite.status === 'accepted' && 'text-fairway',
                      invite.status === 'declined' && 'text-flag',
                      invite.status === 'invited' && 'text-ink-soft',
                    )}
                  >
                    {invite.status}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/* The MVP has no confirm step of its own, so a `confirmed` outing
              still needs its booking made — it keeps the finalize row
              alongside `proposed`. */}
          {(outing.status === 'proposed' || outing.status === 'confirmed') && (
            <>
              <BookRow outing={outing} onBook={onBook} />
              <FinalizeRow outing={outing} onFinalize={onFinalize} />
            </>
          )}

          {outing.status === 'booked' && (
            <p className="mt-3.5 border-t border-brass/25 pt-3.5 text-[13.5px] text-ink">
              Booked
              {outing.booking_ref ? (
                <>
                  {' '}
                  · ref <b className="font-semibold text-pine">{outing.booking_ref}</b>
                </>
              ) : null}
              {outing.booking_url && (
                <>
                  {' · '}
                  <a
                    href={outing.booking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-fairway"
                  >
                    Book at course ↗
                  </a>
                </>
              )}
            </p>
          )}
        </>
      )}
    </li>
  )
}

/**
 * One tee-time slot as a card chip: the clock set large, then seat dots (a
 * taken seat is a brass ring, not an absence), price, and the rate. The date
 * lives in the filter — repeating it on every chip was most of the old
 * sheet's noise.
 */
function SlotChip({
  slot,
  selected,
  onPick,
}: {
  slot: TeeSlot
  selected: boolean
  onPick: () => void
}) {
  const { clock, meridiem } = chipClock(slot.tee_off_at)
  const open = Math.max(0, Math.min(slot.open_slots, MAX_PARTY_SIZE))
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={selected}
      aria-label={`${clock} ${meridiem} · ${formatCents(slot.price_cents)} a head · ${open} of ${MAX_PARTY_SIZE} seats · ${slot.holes} holes`}
      className={cn(
        'min-w-[92px] rounded-club-sm border px-3 pb-1.5 pt-2 text-left transition-colors',
        selected ? 'border-pine bg-pine' : 'border-sage-soft bg-parchment hover:border-fairway',
      )}
    >
      <span
        className={cn(
          'block font-display text-[17px] leading-none tabular-nums',
          selected ? 'text-paper' : 'text-pine',
        )}
      >
        {clock}
        <small
          className={cn(
            'ml-1 align-middle font-sans text-[9.5px] tracking-[0.1em]',
            selected ? 'text-sage' : 'text-ink-soft',
          )}
        >
          {meridiem}
        </small>
      </span>
      <span className="mt-1.5 flex items-center gap-2">
        <span className="flex gap-[2.5px]" aria-hidden="true">
          {Array.from({ length: 4 }, (_, i) => (
            <i
              key={i}
              className={cn(
                'h-1.5 w-1.5 rounded-full',
                i < open
                  ? selected
                    ? 'bg-gilt'
                    : 'bg-pine'
                  : 'border border-brass bg-transparent',
              )}
            />
          ))}
        </span>
        <span
          className={cn('text-[11.5px] tabular-nums', selected ? 'text-sage-soft' : 'text-ink-soft')}
        >
          {formatCents(slot.price_cents)}
        </span>
        <span
          className={cn(
            'text-[9px] uppercase tracking-[0.14em]',
            selected ? 'text-sage' : 'text-brass-deep',
          )}
        >
          {slot.holes}H
        </span>
      </span>
    </button>
  )
}

export function OutingsScreen() {
  usePageTitle('Outings')
  const { isAuthenticated } = useAuth()

  const today = new Date()
  const [date, setDate] = useState(() => toDateInputValue(nextSaturday(new Date())))
  const [holesFilter, setHolesFilter] = useState<'all' | 9 | 18>('all')
  const [location, setLocation] = useState<PickedLocation>(AREA_PRESETS[0])
  const [geoError, setGeoError] = useState<string | null>(null)

  const [courses, setCourses] = useState<GolfCourse[]>([])
  const [coursesLoading, setCoursesLoading] = useState(false)
  const [coursesError, setCoursesError] = useState<string | null>(null)

  /** The picked tee time the rail's create form composes. */
  const [picked, setPicked] = useState<{ courseKey: string; teeOffAt: string } | null>(null)

  /**
   * The one course whose sheet is unfolded (the-outings card): every other
   * course tells its day in the strip and a count. Ten unfolded sheets is a
   * five-thousand-pixel wall; one is a page.
   */
  const [unfoldedCourseKey, setUnfoldedCourseKey] = useState<string | null>(null)
  // Where the row you clicked sat in the viewport, captured on the click and
  // spent on the very next layout — see the unfold button and the effect below.
  const foldAnchor = useRef<{ el: HTMLElement; top: number } | null>(null)

  /**
   * The outing a create just put on the book. In the sticky rail the new card
   * appears right where the form stood (`nearest` scrolls nothing); below the
   * wrap point the card is off-screen and the scroll answers "did that work?".
   */
  const [justProposedId, setJustProposedId] = useState<string | null>(null)
  const justProposedRef = useRef<HTMLLIElement | null>(null)
  useEffect(() => {
    if (!justProposedId) return
    // Instant, not smooth — Chrome drops smooth scrollIntoView in some window
    // states, and a dropped scroll leaves the propose looking like a dead button.
    justProposedRef.current?.scrollIntoView({ block: 'nearest' })
  }, [justProposedId])

  const { outings, isLoading: outingsLoading, error: outingsError, create, finalize, book } =
    useOutings(isAuthenticated)

  // Standing tee-time orders. Its own hook and its own section: the desk above
  // is one member picking one tee, this is the club doing it for them — and
  // the two never share state, because only one of them ends at a card.
  const standing = useStandingTeeTimes(isAuthenticated)

  /**
   * The one rail row showing its detail (single-open disclosure — the rail
   * stays a viewport-sized summary). `undefined` = no explicit choice yet:
   * fall back to the just-proposed row, or the only row when there is one.
   */
  const [openOutingId, setOpenOutingId] = useState<string | null | undefined>(undefined)
  const expandedOutingId =
    openOutingId !== undefined
      ? openOutingId
      : (justProposedId ?? (outings.length === 1 ? outings[0].id : null))

  const fetchCourses = useCallback(async () => {
    setCoursesLoading(true)
    setCoursesError(null)
    setPicked(null)
    setUnfoldedCourseKey(null)
    try {
      const data = await api.get<GolfCoursesResponse>(
        `/golf/courses?lat=${location.lat}&lng=${location.lng}&date=${date}`,
      )
      setCourses(data.courses)
    } catch (e) {
      setCoursesError(e instanceof Error ? e.message : 'Could not read the tee sheets.')
    } finally {
      setCoursesLoading(false)
    }
  }, [location.lat, location.lng, date])

  useEffect(() => {
    if (!isAuthenticated) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- fetch on date/location change, the house data-hook pattern (see hooks/useClubMembership)
    void fetchCourses()
  }, [isAuthenticated, fetchCourses])

  const useMyLocation = () => {
    if (!('geolocation' in navigator)) {
      setGeoError('This browser keeps its location to itself — pick an area instead.')
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoError(null)
        setLocation({
          label: MY_LOCATION_LABEL,
          lat: Math.round(pos.coords.latitude * 10000) / 10000,
          lng: Math.round(pos.coords.longitude * 10000) / 10000,
        })
      },
      () => {
        setGeoError('No location shared — no matter, pick an area instead.')
      },
    )
  }

  const handleCreate = async (body: {
    course_key: string
    tee_off_at: string
    slots: number
    holes: number
    price_cents_per_head: number
    invites: InviteContact[]
  }) => {
    const created = await create(body)
    setPicked(null)
    setJustProposedId(created.id)
    setOpenOutingId(undefined) // the fresh proposal takes the open slot
  }

  const pickedCourse = picked ? courses.find((c) => c.key === picked.courseKey) : undefined
  const pickedSlot = picked
    ? pickedCourse?.slots.find((s) => s.tee_off_at === picked.teeOffAt)
    : undefined

  // Nothing is unfolded on arrival. The list used to auto-open the first course
  // with slots, which cost twice: you landed mid-sheet instead of seeing the
  // day's courses side by side, and because only one sheet opens at a time,
  // unfolding any LOWER course collapsed that first one above it — pulling the
  // row you just clicked up under the cursor.
  const openCourseKey = unfoldedCourseKey ?? picked?.courseKey ?? null

  // Open a sheet, or fold the open one away. Either way the list reflows around
  // the row you clicked — folding removes height, unfolding removes whatever
  // was open elsewhere — so the row is anchored first and restored below.
  const anchorThenFold = (event: React.MouseEvent<HTMLButtonElement>, key: string | null) => {
    const row = event.currentTarget.closest('li')
    if (row) foldAnchor.current = { el: row, top: row.getBoundingClientRect().top }
    setUnfoldedCourseKey(key)
  }

  // Put the clicked row back where it was. useLayoutEffect, not useEffect: this
  // has to run after the new heights are measurable but BEFORE paint, or the
  // correction is a visible jerk instead of no movement at all.
  useLayoutEffect(() => {
    const anchor = foldAnchor.current
    foldAnchor.current = null
    if (!anchor) return

    const drift = anchor.el.getBoundingClientRect().top - anchor.top
    if (drift !== 0) window.scrollBy(0, drift)
  }, [openCourseKey])

  return (
    <ClubPage chrome="clubhouse">
      <PageHead
        eyebrow="The Outings"
        title="Get a game"
        emphasis="together."
        lede="Find a tee time nearby, send the invitations, and mark it booked at the course. The club keeps the thread; the course keeps the tee."
      />

      {!isAuthenticated ? (
        <section className="pb-16">
          <Container>
            <SignInPrompt what="the outings desk — tee times nearby, your invitations, and the plans you have going" />
          </Container>
        </section>
      ) : (
        <>
          {/* ── a. Find a time ── */}
          <section className="pb-16">
            <Container>
              <AreaSectionHead eyebrow="First" title="Find a time." />

              <div className="mb-6 flex flex-wrap items-end gap-4">
                <div>
                  <label htmlFor="outing-date" className={labelClass}>
                    Date
                  </label>
                  <input
                    id="outing-date"
                    type="date"
                    value={date}
                    min={toDateInputValue(today)}
                    max={toDateInputValue(daysFrom(today, 10))}
                    onChange={(e) => e.target.value && setDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label htmlFor="outing-area" className={labelClass}>
                    Near
                  </label>
                  <select
                    id="outing-area"
                    value={location.label}
                    onChange={(e) => {
                      const preset = AREA_PRESETS.find((p) => p.label === e.target.value)
                      if (preset) setLocation(preset)
                    }}
                    className={inputClass}
                  >
                    {AREA_PRESETS.map((preset) => (
                      <option key={preset.label} value={preset.label}>
                        {preset.label}
                      </option>
                    ))}
                    {location.label === MY_LOCATION_LABEL && (
                      <option value={MY_LOCATION_LABEL}>{MY_LOCATION_LABEL}</option>
                    )}
                  </select>
                </div>

                <ClubButton type="button" variant="ghost" size="sm" onClick={useMyLocation}>
                  Use my location
                </ClubButton>

                <div>
                  <span className={labelClass}>Holes</span>
                  <div
                    role="radiogroup"
                    aria-label="Round length"
                    className="flex overflow-hidden rounded-club border border-ink/20"
                  >
                    {([
                      ['all', 'All'],
                      [18, '18 holes'],
                      [9, '9 holes'],
                    ] as const).map(([value, label]) => (
                      <button
                        key={String(value)}
                        type="button"
                        role="radio"
                        aria-checked={holesFilter === value}
                        onClick={() => setHolesFilter(value)}
                        className={cn(
                          'px-[13px] py-[9px] text-[13.5px] transition-colors',
                          holesFilter === value
                            ? 'bg-pine text-paper'
                            : 'bg-parchment text-ink hover:bg-paper-deep',
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {geoError && <p className="mb-4 text-[12.5px] text-ink-soft">{geoError}</p>}

              {/* Sheet + rail (the-outings card): the sheets scroll the main
                  column while the rail — the draft outing and the book — rides
                  sticky beside them, and WRAPS to full width when the row gets
                  too narrow rather than hiding (golf doc §4). */}
              <div className="flex flex-wrap items-start gap-8">
                <div className="min-w-0 flex-1 basis-[520px]">
                  {coursesLoading && (
                    <p className="text-[13.5px] text-ink-soft">Reading the tee sheets…</p>
                  )}
                  {coursesError && (
                    <p role="alert" className="text-[13.5px] text-flag">
                      {coursesError}
                    </p>
                  )}
                  {!coursesLoading && !coursesError && courses.length === 0 && (
                    <EmptyPlate
                      title="No tee times for that day."
                      description="Nothing open near there on that date. Try another day, or another corner of the city."
                    />
                  )}

                  <ul className="flex flex-col gap-4">
                    {courses.map((course) => (
                      <li
                        key={course.key}
                        className="rounded-club-lg border border-ink/12 bg-parchment p-6"
                      >
                        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                          <h3 className="font-display text-[20px] font-medium text-pine">
                            {course.name}
                          </h3>
                          <span className="text-[12.5px] text-ink-soft">
                            {course.city} · {course.distance_miles} mi · {course.operator}
                          </span>
                        </div>
                        {course.sheet_status === 'unlisted' && (
                          <p className="mt-3 text-[13px] text-ink-soft">
                            The club can&apos;t read this course&apos;s sheet —{' '}
                            <a
                              href={course.booking_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-fairway underline underline-offset-2"
                            >
                              book at the course ↗
                            </a>
                          </p>
                        )}
                        {course.sheet_status === 'error' && (
                          <p className="mt-3 text-[13px] text-ink-soft">
                            Couldn&apos;t read the course&apos;s sheet just now —{' '}
                            <a
                              href={course.booking_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-fairway underline underline-offset-2"
                            >
                              book at the course ↗
                            </a>
                          </p>
                        )}
                        {course.slots.length === 0 && course.sheet_opens_on && (
                          <p className="mt-3 text-[13px] text-ink-soft">
                            {course.sheet_opens_on === toDateInputValue(today)
                              ? `The sheet for this day opens today at the course's release time`
                              : `The sheet for this day opens ${new Date(
                                  `${course.sheet_opens_on}T12:00:00`,
                                ).toLocaleDateString(undefined, {
                                  weekday: 'long',
                                  month: 'short',
                                  day: 'numeric',
                                })}`}
                            {' — '}the course books {course.booking_window_days} days out.
                          </p>
                        )}
                        {course.slots.length > 0 &&
                          holesFilter !== 'all' &&
                          course.slots.every((slot) => slot.holes !== holesFilter) && (
                            <p className="mt-3 text-[13px] text-ink-soft">
                              No {holesFilter}-hole starts on this sheet — switch the holes filter
                              to see the day.
                            </p>
                          )}
                        {(() => {
                          const visible = course.slots.filter(
                            (slot) => holesFilter === 'all' || slot.holes === holesFilter,
                          )
                          if (visible.length === 0) return null
                          const pickedHere =
                            picked?.courseKey === course.key ? picked.teeOffAt : null
                          return (
                            <>
                              <DayStrip slots={visible} pickedTeeOffAt={pickedHere} />
                              {course.key === openCourseKey ? (
                                <>
                                  {BANDS.map(({ label }) => {
                                  const bandSlots = visible.filter(
                                    (slot) => bandLabel(slot.tee_off_at) === label,
                                  )
                                  if (bandSlots.length === 0) return null
                                  return (
                                    <div key={label} className="mt-4">
                                      <div className="mb-2.5 flex items-center gap-2.5">
                                        <span className="text-[10px] uppercase tracking-[0.2em] text-brass-deep">
                                          {label}
                                        </span>
                                        <span className="h-px flex-1 bg-brass/55" />
                                        <span className="text-[11px] text-ink-soft">
                                          {bandSlots.length} open
                                        </span>
                                      </div>
                                      <div className="flex flex-wrap gap-2">
                                        {bandSlots.map((slot) => (
                                          <SlotChip
                                            key={slot.tee_off_at}
                                            slot={slot}
                                            selected={pickedHere === slot.tee_off_at}
                                            onPick={() =>
                                              setPicked({
                                                courseKey: course.key,
                                                teeOffAt: slot.tee_off_at,
                                              })
                                            }
                                          />
                                        ))}
                                      </div>
                                    </div>
                                  )
                                  })}
                                  {/* Fold it back. Withheld while a slot from
                                      THIS course is picked: the draft outing is
                                      built from this sheet, and folding it away
                                      under the form reads as losing the draft.
                                      "Never mind" in the rail is that exit. */}
                                  {picked?.courseKey !== course.key && (
                                    <button
                                      type="button"
                                      onClick={(event) => anchorThenFold(event, null)}
                                      className="mt-4 text-[13px] text-ink-soft underline underline-offset-[3px] hover:text-fairway"
                                    >
                                      Fold the sheet
                                    </button>
                                  )}
                                </>
                              ) : (
                                <button
                                  type="button"
                                  onClick={(event) => anchorThenFold(event, course.key)}
                                  className="mt-3 text-[13px] text-fairway underline underline-offset-[3px]"
                                >
                                  {visible.length} open times — unfold the sheet
                                </button>
                              )}
                            </>
                          )
                        })()}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* ── b. The rail: the draft outing, then the book ── */}
                {/* Two scrollers, never mixed: the rail caps at the viewport
                    and scrolls its own overflow (a taller-than-viewport sticky
                    would pin its top and cut the rest off), and
                    overscroll-contain keeps a rail scroll from chaining into
                    the page — the wheel moves whichever column it's over,
                    full stop. */}
                <aside
                  className="sticky top-24 max-h-[calc(100vh-7rem)] flex-[0_0_clamp(230px,28vw,330px)] overflow-y-auto overscroll-contain pr-1 max-[860px]:static max-[860px]:max-h-none max-[860px]:flex-[1_1_100%] max-[860px]:overflow-visible max-[860px]:pr-0"
                  aria-label="Your outings"
                >
                  {/* ONE thing at a time — the draft while composing, the book
                      otherwise. Stacking both is what made the rail outgrow
                      the viewport and sprout the inner scrollbar mid-flow. */}
                  {pickedCourse && pickedSlot ? (
                    <>
                      <div className="rounded-club-lg border border-brass/45 bg-parchment p-5">
                        <p className={labelClass}>The outing, so far</p>
                        <CreateOutingForm
                          course={pickedCourse}
                          slot={pickedSlot}
                          onCancel={() => setPicked(null)}
                          onCreate={handleCreate}
                        />
                      </div>
                      {outings.length > 0 && (
                        <p className="mt-4 text-[13px] text-ink-soft">
                          Your outings · {outings.length} — back when this one is sent.
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <h3 className="mb-4 font-display text-[20px] font-medium text-pine">
                        Your outings.
                      </h3>
                      {outingsLoading && (
                        <p className="text-[13.5px] text-ink-soft">Opening the book…</p>
                      )}
                      {outingsError && (
                        <p role="alert" className="text-[13.5px] text-flag">
                          {outingsError}
                        </p>
                      )}
                      {!outingsLoading && !outingsError && outings.length === 0 && (
                        <EmptyPlate
                          title="No outings on the book."
                          description="Pick a tee time from the sheet and send the first invitations — the book opens with your first plan."
                        />
                      )}

                      {outings.length > 0 && (
                        <ul className="flex flex-col gap-3">
                          {outings.map((outing) => (
                            <OutingCard
                              key={outing.id}
                              outing={outing}
                              justProposed={outing.id === justProposedId}
                              open={outing.id === expandedOutingId}
                              onToggle={() =>
                                setOpenOutingId(outing.id === expandedOutingId ? null : outing.id)
                              }
                              cardRef={outing.id === justProposedId ? justProposedRef : undefined}
                              onFinalize={async (id, ref) => {
                                await finalize(id, ref)
                              }}
                              onBook={book}
                            />
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </aside>
              </div>
            </Container>
          </section>

          {/* ── c. Standing tee times ──
              Below the sheet on purpose. Picking a tee is what a member came
              here to do; a recurring order is what they set once and then
              read. */}
          <section className="pb-16">
            <Container>
              <AreaSectionHead eyebrow="Standing" title="The same tee, every week." />
              <p className="mb-6 max-w-[62ch] text-[14.5px] text-ink-soft">
                A standing order watches the sheet and claims a matching tee the
                moment the course opens the day. It never pays — you confirm it
                with your card, the same as any outing.
              </p>
              <StandingTeeTimes
                courses={courses}
                rules={standing.rules}
                runnerEnabled={standing.runnerEnabled}
                isLoading={standing.isLoading}
                error={standing.error}
                onCreate={standing.create}
                onPause={standing.pause}
                onResume={standing.resume}
                onDelete={standing.destroy}
              />
            </Container>
          </section>
        </>
      )}
    </ClubPage>
  )
}
